import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, SyntheticEvent } from 'react';

import ApiService from '../../../ApiService';
import Column from '../../../components/Column';
import usePageTitle from '../../../hooks/usePageTitle';

import { Campaign } from '../../../types/Campaign';
import { CampaignPageProvider } from '../../../hooks/Contexts/Pages/CampaignPageContext';
import CampaignPageContent from './partials/CampaignPageContent';
import CampaignPageHeader from './partials/CampaignPageHeader';
import { PAGE_TABS, isTabSlug, type TabSlug } from './partials/Tabs/CampaignPageTabs';

export function useCampaign(campaignId?: number | null) {
    return useQuery<Campaign>({
        queryKey: ['campaign', campaignId],
        queryFn: async () => {
            if (!campaignId) throw new Error('No campaign ID provided');
            const response = await ApiService.getCampaign(campaignId);
            return response.data;
        },
        enabled: !!campaignId,
        refetchOnWindowFocus: false,
    });
}

export default function CampaignPage() {
    const params = useParams();
    const navigate = useNavigate();
    const campaignId = params.id ? +params.id : null;

    const { data: campaign } = useCampaign(campaignId);

    const initialTabId = params.tab && isTabSlug(params.tab) ? params.tab : 'overview';
    const [selectedTab, setSelectedTab] = useState<TabSlug>(initialTabId);

    // Build and render title
    const campaignName = campaign?.campaignName;
    const tabName = PAGE_TABS[selectedTab].label;
    const advertiserName = campaign?.dealer?.dealerName ?? '';
    usePageTitle({ entity: campaignName, segments: [tabName, advertiserName] });

    // Handle URL synchronization
    useEffect(() => {
        if (!params.tab) return;

        if (!isTabSlug(params.tab)) {
            navigate(`/campaign/${params.id}/overview`, { replace: true });
        } else {
            setSelectedTab(params.tab);
        }
    }, [params.tab, navigate, params.id]);

    function selectTab(_event: SyntheticEvent, value: TabSlug) {
        setSelectedTab(value);

        if (params.id != null) {
            navigate(`/campaign/${params.id}/${value}`);
        }
    }

    if (!campaign) {
        return null;
    }

    return (
        <CampaignPageProvider initialCampaign={campaign} selectedTab={selectedTab} selectTab={selectTab}>
            <Column gap={0} flex={1}>
                <CampaignPageHeader campaign={campaign} />

                <CampaignPageContent campaign={campaign} />
            </Column>
        </CampaignPageProvider>
    );
}
